<script setup lang="ts">
import type { AnalysisResult } from "@/types/services/analysis/analysis.type.ts";
import { useI18n } from "vue-i18n";
import { computed, ref } from "vue";

const { t } = useI18n();

const props = defineProps<{ analysis: AnalysisResult }>();

const emit = defineEmits<{
  (e: "validate", payload: { valid: boolean; serialNumber?: string }): void;
}>();

const isEditingSerial = ref(false);
const isSerialValid = computed(() => {
  if (!isEditingSerial.value) return true;
  return editedSerialNumber.value.trim().length > 0;
});
const isActionDisabled = computed(() => {
  return isEditingSerial.value && !isSerialValid.value;
});
const isOtherSerialInitialState = computed(() => {
  return (
    props.analysis.analysis.serial_number === "OTHER" && !isEditingSerial.value
  );
});

const editedSerialNumber = ref<string>(
  props.analysis.analysis.serial_number ?? "",
);

const displayValue = (value: unknown) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "None"
  ) {
    return t("views.captureView.validation.notAvailable");
  }
  return value;
};

const confirmValidation = (valid: boolean) => {
  emit("validate", {
    valid,
    serialNumber: isEditingSerial.value
      ? editedSerialNumber.value
      : (props.analysis.analysis.serial_number?.toString() ?? undefined),
  });
};
</script>

<template>
  <div class="validation-sheet">
    <h2 class="sheet-title">
      {{ t("views.captureView.validation.title") }}
    </h2>

    <div class="product-card">
      <div class="product-field highlight">
        <span class="label">
          {{ t("views.captureView.validation.brandName") }}
        </span>
        <strong class="value">
          {{ displayValue(analysis.analysis.brand_name) }}
        </strong>
      </div>

      <div class="product-field">
        <span class="label">
          {{ t("views.captureView.validation.serialNumber") }}
        </span>

        <strong v-if="!isEditingSerial" class="value">
          {{ displayValue(analysis.analysis.serial_number) }}
        </strong>

        <v-text-field
          v-else
          v-model="editedSerialNumber"
          density="compact"
          hide-details
          variant="outlined"
          :placeholder="
            t('views.captureView.validation.serialNumberPlaceholder')
          "
        />
      </div>

      <div class="product-field">
        <span class="label">
          {{ t("views.captureView.validation.productName") }}
        </span>
        <strong class="value">
          {{ displayValue(analysis.analysis.product_name_boomerang) }}
          <span class="sub-value">
            {{ " - " + displayValue(analysis.analysis.code_ref_fabricant) }}
          </span>
        </strong>
      </div>

      <div class="product-field">
        <span class="label">
          {{ t("views.captureView.validation.categoryName") }}
        </span>
        <span class="value">
          {{ displayValue(analysis.analysis.category_name) }}
        </span>
      </div>

      <div class="product-field">
        <span class="label">
          {{ t("views.captureView.validation.subCategoryName") }}
        </span>
        <span class="value">
          {{ displayValue(analysis.analysis.sub_category_name) }}
        </span>
      </div>
    </div>

    <!-- Actions -->
    <div class="sheet-actions">
      <v-btn
        block
        :variant="isOtherSerialInitialState ? undefined : 'outlined'"
        :class="[
          'btn-pill',
          isOtherSerialInitialState ? 'btn-primary' : 'btn-outline',
        ]"
        :disabled="isEditingSerial"
        @click="isEditingSerial = true"
      >
        {{ t("views.captureView.validation.serialWrong") }}
      </v-btn>

      <v-divider v-if="!isOtherSerialInitialState" class="divider"></v-divider>

      <v-btn
        v-if="!isOtherSerialInitialState"
        block
        class="btn-primary btn-pill"
        :disabled="isActionDisabled"
        @click="confirmValidation(true)"
      >
        {{ t("views.captureView.validation.confirm") }}
      </v-btn>

      <v-btn
        block
        variant="outlined"
        class="btn-outline btn-pill"
        :disabled="isActionDisabled"
        @click="confirmValidation(false)"
      >
        {{ t("views.captureView.validation.reject") }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped>
.divider {
  width: 50%;
  align-self: center;
  margin: 10px 0;
}
.validation-sheet {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.sheet-title {
  font-size: 18px;
  font-weight: 600;
}

.product-card {
  border: 1px solid #dadada;
  background-color: #fafafa;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.product-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.value {
  font-size: 14px;
  color: #111827;
}

.sub-value {
  font-weight: 400;
}

.product-field.highlight .value {
  color: #629d1b;
  font-weight: 600;
}

.sheet-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn-pill {
  border-radius: 999px !important;
  height: 48px;
  font-weight: 600;
}

.btn-edit-serial {
  align-self: flex-start;
  margin-top: 4px;
  padding: 0;
  min-width: auto;
  font-size: 12px;
  text-transform: none;
}
</style>
